import { requireAPIKey } from "../action-helpers.js";
import { apiDelete, apiGetJSON, apiPostJSON, apiPostMultipartDetailed, apiPostSSE } from "../../client.js";
import { isAbortError, requestsCurrent } from "../../core/request-coordinator.js";
import {
  inferenceCapabilityAttachmentInputType,
  inferenceCapabilityFileAccept,
  inferenceCapabilityFileInputName,
  inferenceCapabilityMediaKind,
  inferenceCapabilitySupportsContextRefs,
  normalizeInferenceCapability
} from "../../inference-capabilities.js";
import { loadThreadComponent } from "../snapshot.js";
import { activeThreadTurnFromState, threadSendBlockReason } from "../state.js";
import { llmCapabilitySendBlockReason } from "./capability-resolution.js";
import { captureImageFromCamera, captureScreenshot, startMicrophoneCapture } from "./browser-media.js";
import { filterLLMContextItems, loadLLMThreadContextItems, MAX_LLM_CONTEXT_ITEM_REFS } from "./context-items.js";
import {
  clearContextItemUploadFiles,
  clearThreadCaptureSession,
  contextItemUploadFiles,
  forgetPendingAttachmentFile,
  pendingAttachmentFile,
  rememberPendingAttachmentFile,
  setContextItemUploadFiles,
  setThreadCaptureSession,
  threadCaptureSession
} from "../transient-media.js";

function requireContextItemWrite(options = {}) {
  if (!canWriteContextPermission(options.contextPermissions, "contextItems")) {
    throw new Error("context item changes are not allowed in this embed session.");
  }
}

function requireContextPinWrite(options = {}) {
  if (!canWriteContextPermission(options.contextPermissions, "contextPins")) {
    throw new Error("context pin changes are not allowed in this embed session.");
  }
}

function requireAttachmentUpload(options = {}) {
  if (!canUploadAttachmentPermission(options.contextPermissions)) {
    throw new Error("attachment uploads are not allowed in this embed session.");
  }
}

function canWriteContextPermission(permissions, key) {
  if (!permissions) {
    return true;
  }
  return String(permissions[key] || "").trim().toLowerCase() === "write";
}

function canUploadAttachmentPermission(permissions) {
  if (!permissions) {
    return true;
  }
  return String(permissions.attachments || "").trim().toLowerCase() === "upload";
}

export function handleLLMThreadComponentInput(state, target) {
  if (target instanceof HTMLSelectElement && target.name === "capability_family") {
    const capability = normalizeInferenceCapability(target.value);
    state.composerCapability = capability;
    state.composerRouteOverride = "";
    state.composerModelOverride = "";
    state.composerOverrideOpen = false;
    prunePendingAttachmentsForCapability(state, capability);
    return true;
  }
  if ((target instanceof HTMLInputElement || target instanceof HTMLSelectElement) && target.name === "route_id") {
    state.composerRouteOverride = target.value;
    return true;
  }
  if (target instanceof HTMLInputElement && target.name === "model") {
    state.composerModelOverride = target.value;
    return true;
  }
  if (target instanceof HTMLInputElement && target.name === "context_title") {
    state.contextItemDraftTitle = target.value;
    return true;
  }
  if (target instanceof HTMLTextAreaElement && target.name === "context_text") {
    state.contextItemDraftText = target.value;
    return true;
  }
  if (target instanceof HTMLSelectElement && target.name === "context_content_type") {
    state.contextItemDraftContentType = target.value === "text/markdown" ? "text/markdown" : "text/plain";
    return true;
  }
  if (target instanceof HTMLInputElement && target.name === "context_filter_text") {
    state.contextItemFilterText = target.value;
    return true;
  }
  if (target instanceof HTMLInputElement && target.type === "file" && target.name === "context_file") {
    const files = target.files ? [...target.files].filter(Boolean) : [];
    setContextItemUploadFiles(state, files);
    state.contextItemUploadFilename = files.map((file) => file.name).join(", ");
    target.value = "";
    return true;
  }
  const currentCapability = normalizeInferenceCapability(state.composerCapability);
  const expectedInputName = inferenceCapabilityFileInputName(currentCapability);
  if (target instanceof HTMLInputElement && target.type === "file" && target.name === expectedInputName) {
    const files = target.files ? [...target.files].filter(Boolean) : [];
    if (files.length > 0) {
      addPendingFiles(state, files, fileInputSource(target), currentCapability);
      target.value = "";
    }
    return true;
  }
  return false;
}

export async function handleLLMThreadComponentSubmit(state, form, options = {}) {
  if (!(form instanceof HTMLFormElement)) {
    return false;
  }
  if (form.id !== "thread-message-form" || !form.closest("[data-thread-component]")) {
    return false;
  }
  const apiKey = options.apiKey;
  requireAPIKey(apiKey);
  const threadItem = state.thread;
  if (!threadItem) {
    throw new Error("Select a thread first.");
  }
  const capability = normalizeInferenceCapability(state.composerCapability);
  const prompt = String(state.composerDraft || "").trim();
  const routeOverride = String(state.composerRouteOverride || "").trim();
  const modelOverride = String(state.composerModelOverride || "").trim();
  const pendingAttachments = pendingAttachmentsForCapability(state, capability);
  const imageFiles = capability === "responses"
    ? pendingAttachments.map((item) => pendingAttachmentFile(state, item.id)).filter(Boolean)
    : [];
  const audioFiles = capability === "audio_transcription" || capability === "audio_translation"
    ? pendingAttachments.map((item) => pendingAttachmentFile(state, item.id)).filter(Boolean)
    : [];
  validateComposerSubmission(capability, prompt, imageFiles, audioFiles);
  validateComposerContextLimits(state, capability);

  const activeTurn = activeThreadTurnFromState(state);
  const sendBlockReason = threadSendBlockReason(state, threadItem, activeTurn);
  if (sendBlockReason) {
    throw new Error(sendBlockReason);
  }
  const capabilityBlockReason = llmCapabilitySendBlockReason(state, capability);
  if (capabilityBlockReason) {
    throw new Error(capabilityBlockReason);
  }

  const promptField = form.elements.namedItem("prompt");
  const sendButton = form.querySelector(".composer-v2__send");
  state.submitting = true;
  state.composerDraft = "";
  if (promptField instanceof HTMLTextAreaElement) {
    promptField.value = "";
  }
  if (sendButton instanceof HTMLButtonElement) {
    sendButton.disabled = true;
  }

  const selectedPendingAttachments = pendingAttachments;
  const selectedFiles = selectedPendingAttachments
    .map((item) => pendingAttachmentFile(state, item.id))
    .filter(Boolean);
  if (selectedFiles.length > 0) {
    requireAttachmentUpload(options);
  }
  const localAttachments = selectedPendingAttachments.map((item) => pendingAttachmentToLocalAttachment(item, threadItem.id));
  const placeholderTurn = createPlaceholderTurn(capability, prompt, localAttachments);
  state.turns = [...state.turns, placeholderTurn];
  state.streamState = "connecting";
  options.render?.();

  let deltaBuffer = "";
  let rafScheduled = false;
  let streamTurnID = "pending";
  let terminalErrorMessage = "";

  function flushDelta() {
    rafScheduled = false;
    if (!requestsCurrent(options.request)) {
      deltaBuffer = "";
      return;
    }
    if (!deltaBuffer) {
      return;
    }
    state.turns = state.turns.map((item) =>
      item.id === streamTurnID
        ? { ...item, assistant_message: `${item.assistant_message || ""}${deltaBuffer}` }
        : item
    );
    deltaBuffer = "";
    options.render?.();
  }

  try {
    const uploadedAttachments = [];
    for (const file of selectedFiles) {
      options.setStatus?.(`Uploading ${file.name}...`, "busy");
      const formData = new FormData();
      formData.set("file", file, file.name);
      const response = await apiPostMultipartDetailed(apiKey, `/v1/threads/${encodeURIComponent(threadItem.id)}/attachments`, formData, {
        signal: options.signal
      });
      uploadedAttachments.push(response.body?.attachment || null);
    }
    if (!requestsCurrent(options.request)) {
      return true;
    }

    const uploadedParts = buildUploadedInputParts(capability, prompt, uploadedAttachments, localAttachments);
    state.turns = state.turns.map((item) => item.id === "pending" ? {
      ...item,
      input_parts: uploadedParts
    } : item);
    options.render?.();

    options.setStatus?.(capability === "responses" ? "Streaming..." : "Processing...", "busy");
    await apiPostSSE(apiKey, `/v1/threads/${encodeURIComponent(threadItem.id)}/inputs`, buildSubmitPayload(state, capability, prompt, uploadedAttachments, routeOverride, modelOverride), {
      signal: options.signal,
      onEvent({ payload }) {
        if (!payload || !payload.type || !requestsCurrent(options.request)) {
          return;
        }
        switch (payload.type) {
        case "turn.started":
          state.streamState = "connected";
          if (payload.turn) {
            if (payload.turn.id) {
              streamTurnID = payload.turn.id;
            }
            state.turns = state.turns.map((item) =>
              item.id === "pending" || item.id === streamTurnID ? mergeTurnAttachmentURLs(item, payload.turn) : item
            );
          }
          options.render?.();
          break;
        case "turn.delta":
          deltaBuffer += payload.delta || "";
          if (!rafScheduled) {
            rafScheduled = true;
            requestAnimationFrame(flushDelta);
          }
          break;
        case "turn.usage":
          break;
        case "turn.completed":
          flushDelta();
          if (payload.turn) {
            if (payload.turn.id) {
              streamTurnID = payload.turn.id;
            }
            state.turns = state.turns.map((item) =>
              item.id === "pending" || item.id === streamTurnID ? mergeTurnAttachmentURLs(item, payload.turn) : item
            );
          }
          state.streamState = "idle";
          options.setStatus?.(capability === "responses" ? "Response received." : "Result received.", "ok");
          options.render?.();
          break;
        case "turn.canceled":
          flushDelta();
          if (payload.turn) {
            if (payload.turn.id) {
              streamTurnID = payload.turn.id;
            }
            state.turns = state.turns.map((item) =>
              item.id === "pending" || item.id === streamTurnID ? mergeTurnAttachmentURLs(item, payload.turn) : item
            );
          } else {
            state.turns = state.turns.map((item) =>
              item.id === streamTurnID
                ? { ...item, state: "canceled", error_message: payload.error || "Turn canceled." }
                : item
            );
          }
          state.streamState = "idle";
          options.setStatus?.("Turn canceled.", "ok");
          options.render?.();
          break;
        case "turn.failed":
          flushDelta();
          terminalErrorMessage = payload.error || "Turn failed.";
          if (payload.turn) {
            if (payload.turn.id) {
              streamTurnID = payload.turn.id;
            }
            state.turns = state.turns.map((item) =>
              item.id === "pending" || item.id === streamTurnID ? mergeTurnAttachmentURLs(item, payload.turn) : item
            );
          } else {
            state.turns = state.turns.map((item) =>
              item.id === streamTurnID
                ? { ...item, state: "failed", error_message: payload.error || "Turn failed." }
                : item
            );
          }
          state.streamState = "idle";
          options.setStatus?.(terminalErrorMessage, "error");
          options.render?.();
          break;
        }
      }
    });
    flushDelta();
    if (!requestsCurrent(options.request)) {
      return true;
    }
    if (terminalErrorMessage) {
      throw new Error(terminalErrorMessage);
    }
    clearPendingAttachments(state, selectedPendingAttachments.map((item) => item.id));
    state.composerContextRefs = [];
    await options.refreshCollections?.();
    if (requestsCurrent(options.request)) {
      await loadThreadComponent(state, apiKey, threadItem.id, {
        request: options.request,
        includeDiagnostics: options.includeDiagnostics
      });
    }
    return true;
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(options.request)) {
      state.turns = state.turns.filter((item) => item.id !== "pending");
      state.streamState = "idle";
      return true;
    }
    state.streamState = "error";
    state.composerDraft = prompt;
    if (promptField instanceof HTMLTextAreaElement) {
      promptField.value = prompt;
    }
    state.turns = state.turns.filter((item) => item.id !== "pending");
    throw error;
  } finally {
    state.submitting = false;
    if (requestsCurrent(options.request)) {
      options.render?.();
    }
  }
}

export async function handleLLMThreadComponentClick(state, target, options = {}) {
  if (!(target instanceof Element) || !target.closest("[data-thread-component]")) {
    return false;
  }
  const threadItem = state.thread;
  const threadCancel = target.closest("[data-action='cancel-thread-run']");
  if (threadCancel) {
    await cancelActiveLLMTurn(state, options.apiKey, options);
    return true;
  }
  const toggleOverrides = target.closest("[data-action='toggle-composer-overrides']");
  if (toggleOverrides) {
    state.composerOverrideOpen = !state.composerOverrideOpen;
    options.render?.();
    return true;
  }
  const toggleContext = target.closest("[data-action='toggle-composer-context']");
  if (toggleContext) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    state.composerContextPanelOpen = !state.composerContextPanelOpen;
    options.render?.();
    if (state.composerContextPanelOpen) {
      await loadContextItems(state, options.apiKey, options);
    }
    return true;
  }
  const refreshContext = target.closest("[data-action='refresh-context-items']");
  if (refreshContext) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    await loadContextItems(state, options.apiKey, { ...options, force: true });
    return true;
  }
  const toggleContextFilter = target.closest("[data-action='toggle-context-filter']");
  if (toggleContextFilter) {
    state.contextItemFilterOpen = !state.contextItemFilterOpen;
    if (!state.contextItemFilterOpen) {
      state.contextItemFilterText = "";
    }
    options.render?.();
    return true;
  }
  const closeContextPreview = target.closest("[data-action='close-context-preview']");
  if (closeContextPreview) {
    state.contextItemPreviewID = "";
    state.contextItemPreviewLoading = false;
    state.contextItemPreviewError = "";
    options.render?.();
    return true;
  }
  const previewContextItem = target.closest("[data-action='preview-context-item']");
  if (previewContextItem) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    await openContextItemPreview(state, previewContextItem, options.apiKey, options);
    return true;
  }
  const deleteContextItem = target.closest("[data-action='delete-context-item']");
  if (deleteContextItem) {
    requireContextItemWrite(options);
    const contextItemID = contextItemIDFromTarget(deleteContextItem);
    if (contextItemID) {
      state.contextItemDeleteConfirmID = contextItemID;
      options.render?.();
    }
    return true;
  }
  const cancelDeleteContextItem = target.closest("[data-action='cancel-delete-context-item']");
  if (cancelDeleteContextItem) {
    state.contextItemDeleteConfirmID = "";
    options.render?.();
    return true;
  }
  const confirmDeleteContextItem = target.closest("[data-action='confirm-delete-context-item']");
  if (confirmDeleteContextItem) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    requireContextItemWrite(options);
    await deleteContextItemFromThread(state, confirmDeleteContextItem, options.apiKey, options);
    return true;
  }
  const createContextText = target.closest("[data-action='create-context-text']");
  if (createContextText) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    requireContextItemWrite(options);
    await createTextContextItem(state, options.apiKey, options);
    return true;
  }
  const uploadContextFile = target.closest("[data-action='upload-context-file']");
  if (uploadContextFile) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    requireContextItemWrite(options);
    await uploadContextItemFiles(state, uploadContextFile, options.apiKey, options);
    return true;
  }
  const referenceAllContextItems = target.closest("[data-action='reference-all-context-items']");
  if (referenceAllContextItems) {
    const shownItems = filterLLMContextItems(state.contextItems || [], state.contextItemFilterText || "");
    let addedCount = 0;
    let skippedByLimit = 0;
    for (const item of shownItems) {
      const ref = buildContextItemRefFromItem(item);
      const blockReason = composerContextRefBlockReason(state, ref);
      if (!blockReason) {
        addComposerContextRef(state, ref);
        addedCount += 1;
      } else if (blockReason === "limit") {
        skippedByLimit += 1;
      }
    }
    if (addedCount > 0) {
      options.render?.();
      const suffix = skippedByLimit > 0 ? ` ${contextItemLimitMessage()}` : "";
      options.setStatus?.(`Added ${addedCount} context item${addedCount === 1 ? "" : "s"} to next turn.${suffix}`, "ok");
    } else if (skippedByLimit > 0) {
      options.setStatus?.(contextItemLimitMessage(), "error");
    } else {
      options.setStatus?.(shownItems.length ? "All shown context items are already active." : "No context items to add.", "idle");
    }
    return true;
  }
  const removeAllContextItems = target.closest("[data-action='remove-all-context-items']");
  if (removeAllContextItems) {
    const shownItemIDs = new Set(filterLLMContextItems(state.contextItems || [], state.contextItemFilterText || "")
      .map((item) => String(item?.id || "").trim())
      .filter(Boolean));
    const removedCount = removeComposerContextItemsByID(state, shownItemIDs);
    if (removedCount > 0) {
      options.render?.();
      options.setStatus?.(`Removed ${removedCount} context item${removedCount === 1 ? "" : "s"} from this turn.`, "ok");
    } else {
      options.setStatus?.(shownItemIDs.size ? "No shown queued context items to clear." : "No context items to clear.", "idle");
    }
    return true;
  }
  const referenceContextItem = target.closest("[data-action='reference-context-item']");
  if (referenceContextItem) {
    const ref = buildContextItemRef(state, referenceContextItem);
    if (!ref) {
      return true;
    }
    const blockReason = composerContextRefBlockReason(state, ref);
    if (!blockReason) {
      addComposerContextRef(state, ref);
      options.render?.();
      options.setStatus?.("Added context item to next turn.", "ok");
    } else if (blockReason === "limit") {
      options.setStatus?.(contextItemLimitMessage(), "error");
    } else {
      options.setStatus?.("That context item is already active.", "idle");
    }
    return true;
  }
  const insertContextItem = target.closest("[data-action='insert-context-item']");
  if (insertContextItem) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    await insertContextItemIntoComposer(state, insertContextItem, options.apiKey, options);
    return true;
  }
  const triggerFileInput = target.closest("[data-action='trigger-file-input']");
  if (triggerFileInput) {
    requireAttachmentUpload(options);
    const form = triggerFileInput.closest("form");
    const fileInput = form?.querySelector("input[type='file'][data-file-input]");
    if (fileInput) {
      fileInput.click();
    }
    return true;
  }
  const captureCamera = target.closest("[data-action='capture-camera-image']");
  if (captureCamera) {
    requireAttachmentUpload(options);
    state.captureState = {
      kind: "image",
      status: "capturing",
      source: "camera"
    };
    options.render?.();
    try {
      const file = await (options.captureImageFromCamera || captureImageFromCamera)();
      addPendingFiles(state, [file], "camera", "responses");
      options.setStatus?.("Captured image from camera.", "ok");
      return true;
    } finally {
      state.captureState = {
        kind: "",
        status: "idle",
        source: ""
      };
      options.render?.();
    }
  }
  const captureScreenshotTarget = target.closest("[data-action='capture-screenshot']");
  if (captureScreenshotTarget) {
    requireAttachmentUpload(options);
    state.captureState = {
      kind: "image",
      status: "capturing",
      source: "screenshot"
    };
    options.render?.();
    try {
      const file = await (options.captureScreenshot || captureScreenshot)();
      addPendingFiles(state, [file], "screenshot", "responses");
      options.setStatus?.("Captured screenshot.", "ok");
      return true;
    } finally {
      state.captureState = {
        kind: "",
        status: "idle",
        source: ""
      };
      options.render?.();
    }
  }
  const startAudioCapture = target.closest("[data-action='start-audio-capture']");
  if (startAudioCapture) {
    requireAttachmentUpload(options);
    if (threadCaptureSession(state)) {
      return true;
    }
    state.captureState = {
      kind: "audio",
      status: "capturing",
      source: "microphone"
    };
    options.render?.();
    let session = null;
    try {
      session = await (options.startMicrophoneCapture || startMicrophoneCapture)();
    } catch (error) {
      state.captureState = {
        kind: "",
        status: "idle",
        source: ""
      };
      options.render?.();
      throw error;
    }
    setThreadCaptureSession(state, session);
    state.captureState = {
      kind: "audio",
      status: "recording",
      source: "microphone"
    };
    options.render?.();
    options.setStatus?.("Recording microphone input...", "busy");
    return true;
  }
  const stopAudioCapture = target.closest("[data-action='stop-audio-capture']");
  if (stopAudioCapture) {
    const session = clearThreadCaptureSession(state);
    if (!session) {
      return true;
    }
    state.captureState = {
      kind: "audio",
      status: "processing",
      source: "microphone"
    };
    options.render?.();
    try {
      const file = await session.finish();
      addPendingFiles(state, [file], "microphone", normalizeInferenceCapability(state.composerCapability));
      options.setStatus?.("Captured microphone audio.", "ok");
      return true;
    } finally {
      state.captureState = {
        kind: "",
        status: "idle",
        source: ""
      };
      options.render?.();
    }
  }
  const removePendingAttachmentTarget = target.closest("[data-action='remove-pending-attachment']");
  if (removePendingAttachmentTarget) {
    const pendingID = String(removePendingAttachmentTarget.getAttribute("data-pending-id") || "").trim();
    if (!pendingID) {
      return true;
    }
    removePendingAttachment(state, pendingID);
    options.render?.();
    options.setStatus?.("Removed pending attachment.", "ok");
    return true;
  }
  const useOutputText = target.closest("[data-action='use-output-text']");
  if (useOutputText) {
    const turnID = String(useOutputText.getAttribute("data-turn-id") || "").trim();
    const outputIndex = Number.parseInt(String(useOutputText.getAttribute("data-output-index") || ""), 10);
    if (!turnID || Number.isNaN(outputIndex)) {
      return true;
    }
    const turn = (state.turns || []).find((item) => item?.id === turnID);
    const part = Array.isArray(turn?.output_parts) ? turn.output_parts[outputIndex] : null;
    const text = typeof part?.text === "string" ? part.text.trim() : "";
    if (!text) {
      return true;
    }
    state.composerDraft = state.composerDraft
      ? `${state.composerDraft.trimEnd()}\n\n${text}`
      : text;
    options.render?.();
    options.setStatus?.("Added output to the composer.", "ok");
    return true;
  }
  const referenceOutputText = target.closest("[data-action='reference-output-text']");
  if (referenceOutputText) {
    const ref = buildOutputTextRef(state, referenceOutputText);
    if (!ref) {
      return true;
    }
    if (addComposerContextRef(state, ref)) {
      options.render?.();
      options.setStatus?.("Added text to next-turn context.", "ok");
    } else {
      options.setStatus?.("That text is already active in context.", "idle");
    }
    return true;
  }
  const referenceAttachment = target.closest("[data-action='reference-attachment']");
  if (referenceAttachment) {
    const ref = buildAttachmentRef(state, referenceAttachment);
    if (!ref) {
      return true;
    }
    if (addComposerContextRef(state, ref)) {
      options.render?.();
      options.setStatus?.("Added image to next-turn context.", "ok");
    } else {
      options.setStatus?.("That image is already active in context.", "idle");
    }
    return true;
  }
  const removeContextRef = target.closest("[data-action='remove-context-ref']");
  if (removeContextRef) {
    const ref = buildContextRefFromDataset(removeContextRef);
    if (!ref) {
      return true;
    }
    if (removeComposerContextRef(state, ref)) {
      options.render?.();
      options.setStatus?.("Removed next-turn context.", "ok");
    }
    return true;
  }
  const pinTarget = target.closest("[data-action='pin-context-ref']");
  if (pinTarget) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    requireContextPinWrite(options);
    const ref = buildContextRefFromDataset(pinTarget);
    if (!ref) {
      return true;
    }
    if (isPinnedContextRef(threadItem, ref)) {
      options.setStatus?.("That item is already pinned.", "idle");
      return true;
    }
    const response = await apiPostJSON(options.apiKey, `/v1/threads/${encodeURIComponent(threadItem.id)}/context/pins`, serializeContextRef(ref), {
      signal: options.signal
    });
    const pin = response?.pin || null;
    if (pin) {
      const nextPinned = [...(threadItem.pinned_context_refs || []), pin];
      state.thread = {
        ...threadItem,
        pinned_context_refs: nextPinned
      };
      removeComposerContextRef(state, ref);
      options.render?.();
    }
    options.setStatus?.("Pinned context to the thread.", "ok");
    return true;
  }
  const unpinTarget = target.closest("[data-action='unpin-context-ref']");
  if (unpinTarget) {
    requireAPIKey(options.apiKey);
    if (!threadItem) {
      throw new Error("Select a thread first.");
    }
    requireContextPinWrite(options);
    const pinID = String(unpinTarget.getAttribute("data-pin-id") || "").trim();
    if (!pinID) {
      return true;
    }
    await apiDelete(options.apiKey, `/v1/threads/${encodeURIComponent(threadItem.id)}/context/pins/${encodeURIComponent(pinID)}`, {
      signal: options.signal
    });
    state.thread = {
      ...threadItem,
      pinned_context_refs: (threadItem.pinned_context_refs || []).filter((item) => String(item?.id || "").trim() !== pinID)
    };
    options.render?.();
    options.setStatus?.("Removed pinned context.", "ok");
    return true;
  }
  return false;
}

async function cancelActiveLLMTurn(state, apiKey, options) {
  requireAPIKey(apiKey);
  const currentThread = state.thread;
  if (!currentThread) {
    return;
  }
  options.setStatus?.("Canceling...", "busy");
  await apiPostJSON(apiKey, `/v1/threads/${encodeURIComponent(currentThread.id)}/cancel`, {});
  await options.refreshCollections?.();
  if (!requestsCurrent(options.request)) {
    return;
  }
  const loadThread = options.loadThreadComponent || loadThreadComponent;
  await loadThread(state, apiKey, currentThread.id, {
    request: options.request,
    includeDiagnostics: options.includeDiagnostics
  });
  if (!requestsCurrent(options.request)) {
    return;
  }
  state.streamState = "idle";
  options.setStatus?.("Turn canceled.", "ok");
  options.render?.();
}

function mergeTurnAttachmentURLs(existing, next) {
  const localObjectURLByAttachmentID = new Map();
  for (const part of existing?.input_parts || []) {
    const attachmentID = String(part?.attachment_id || part?.attachment?.id || "").trim();
    const objectURL = String(part?.attachment?.object_url || "").trim();
    if (attachmentID && objectURL) {
      localObjectURLByAttachmentID.set(attachmentID, objectURL);
    }
  }
  return {
    ...existing,
    ...next,
    input_parts: (next?.input_parts || []).map((part) => {
      const attachmentID = String(part?.attachment_id || part?.attachment?.id || "").trim();
      if (!attachmentID || !part?.attachment || !localObjectURLByAttachmentID.has(attachmentID)) {
        return part;
      }
      return {
        ...part,
        attachment: {
          ...part.attachment,
          object_url: localObjectURLByAttachmentID.get(attachmentID)
        }
      };
    })
  };
}

function validateComposerSubmission(capability, prompt, imageFiles, audioFiles) {
  if (capability === "responses") {
    if (audioFiles.length > 0) {
      throw new Error("responses submissions do not accept audio attachments");
    }
    if (!prompt && imageFiles.length === 0) {
      throw new Error("input_parts must not be empty");
    }
    return;
  }
  if (capability === "audio_transcription" || capability === "audio_translation") {
    if (imageFiles.length > 0) {
      throw new Error("audio submissions do not accept image attachments");
    }
    if (audioFiles.length !== 1) {
      throw new Error("audio submissions require exactly one audio file");
    }
    return;
  }
  if (capability === "audio_speech" || capability === "image_generation") {
    if (imageFiles.length > 0 || audioFiles.length > 0) {
      throw new Error(`${capability} submissions do not accept attachments`);
    }
    if (!prompt) {
      throw new Error("input_parts must not be empty");
    }
    return;
  }
  throw new Error("Unsupported capability family.");
}

function validateComposerContextLimits(state, capability) {
  if (!inferenceCapabilitySupportsContextRefs(capability)) {
    return;
  }
  if (activeContextItemIDSet(state).size > MAX_LLM_CONTEXT_ITEM_REFS) {
    throw new Error(contextItemLimitMessage());
  }
}

function pendingAttachmentToLocalAttachment(item, threadID) {
  const mediaKind = String(item?.media_kind || "").trim();
  const fallbackName = mediaKind === "image" ? "image" : "audio";
  return {
    id: String(item?.id || ""),
    thread_id: threadID,
    origin: String(item?.origin || "upload"),
    media_kind: mediaKind,
    content_type: String(item?.content_type || ""),
    filename: String(item?.filename || fallbackName),
    size_bytes: Number(item?.size_bytes || 0),
    state: "draft",
    object_url: String(item?.object_url || "")
  };
}

function createPlaceholderTurn(capability, prompt, localAttachments) {
  return {
    id: "pending",
    state: "running",
    capability_family: capability,
    input_parts: buildLocalInputParts(capability, prompt, localAttachments),
    output_parts: [],
    prompt,
    assistant_message: "",
    created_at: new Date().toISOString()
  };
}

function buildLocalInputParts(capability, prompt, localAttachments) {
  const attachmentType = attachmentInputTypeForCapability(capability);
  return [
    ...(prompt ? [{ type: "input_text", text: prompt }] : []),
    ...localAttachments.map((attachment) => ({
      type: attachmentType,
      attachment_id: attachment.id,
      attachment
    }))
  ];
}

function buildUploadedInputParts(capability, prompt, uploadedAttachments, localAttachments) {
  const attachmentType = attachmentInputTypeForCapability(capability);
  return [
    ...(prompt ? [{ type: "input_text", text: prompt }] : []),
    ...uploadedAttachments
      .map((attachment, index) => attachment ? {
        type: attachmentType,
        attachment_id: attachment.id,
        attachment: {
          ...attachment,
          object_url: localAttachments[index]?.object_url || ""
        }
      } : null)
      .filter(Boolean)
  ];
}

function buildSubmitInputParts(capability, prompt, uploadedAttachments) {
  const attachmentType = attachmentInputTypeForCapability(capability);
  return [
    ...(prompt ? [{ type: "input_text", text: prompt }] : []),
    ...uploadedAttachments
      .filter(Boolean)
      .map((attachment) => ({
        type: attachmentType,
        attachment_id: attachment.id
      }))
  ];
}

function buildSubmitPayload(state, capability, prompt, uploadedAttachments, routeOverride, modelOverride) {
  const payload = {
    capability_family: capability,
    input_parts: buildSubmitInputParts(capability, prompt, uploadedAttachments)
  };
  if (routeOverride) {
    payload.route_id = routeOverride;
  }
  if (modelOverride) {
    payload.model = modelOverride;
  }
  if (inferenceCapabilitySupportsContextRefs(capability) && Array.isArray(state?.composerContextRefs) && state.composerContextRefs.length > 0) {
    payload.context_refs = state.composerContextRefs.map((item) => serializeContextRef(item));
  }
  if (capability === "audio_speech") {
    payload.options = {
      voice: "alloy",
      response_format: "mp3"
    };
  } else if (capability === "image_generation") {
    payload.options = {
      response_format: "b64_json"
    };
  }
  return payload;
}

function attachmentInputTypeForCapability(capability) {
  return inferenceCapabilityAttachmentInputType(capability);
}

const loadContextItems = loadLLMThreadContextItems;

async function createTextContextItem(state, apiKey, options = {}) {
  const threadID = String(state?.thread?.id || "").trim();
  const title = String(state.contextItemDraftTitle || "").trim();
  const text = String(state.contextItemDraftText || "").trim();
  if (!title) {
    throw new Error("context title is required");
  }
  if (!text) {
    throw new Error("context text is required");
  }
  const response = await apiPostJSON(apiKey, `/v1/threads/${encodeURIComponent(threadID)}/context-items`, {
    title,
    text,
    content_type: state.contextItemDraftContentType === "text/markdown" ? "text/markdown" : "text/plain"
  }, {
    signal: options.signal
  });
  if (!requestsCurrent(options.request)) {
    return;
  }
  if (response?.context_item) {
    upsertContextItem(state, response.context_item);
    state.contextItemsLoadedThreadID = threadID;
  }
  state.contextItemDraftTitle = "";
  state.contextItemDraftText = "";
  state.contextItemDraftContentType = "text/plain";
  options.render?.();
  options.setStatus?.("Added context text.", "ok");
}

async function uploadContextItemFiles(state, target, apiKey, options = {}) {
  const threadID = String(state?.thread?.id || "").trim();
  const fileInput = findContextItemFileInput(target);
  const files = contextItemUploadFiles(state);
  if (files.length === 0 && fileInput?.files) {
    files.push(...[...fileInput.files].filter(Boolean));
  }
  if (files.length === 0) {
    throw new Error("choose a text or markdown file first");
  }
  for (const file of files) {
    const formData = new FormData();
    formData.set("file", file, file.name);
    formData.set("title", titleFromFilename(file.name));
    formData.set("source_path", file.name);
    const response = await apiPostMultipartDetailed(apiKey, `/v1/threads/${encodeURIComponent(threadID)}/context-items`, formData, {
      signal: options.signal
    });
    if (!requestsCurrent(options.request)) {
      return;
    }
    if (response.body?.context_item) {
      upsertContextItem(state, response.body.context_item);
      state.contextItemsLoadedThreadID = threadID;
    }
  }
  if (fileInput) {
    fileInput.value = "";
  }
  clearContextItemUploadFiles(state);
  state.contextItemUploadFilename = "";
  options.render?.();
  options.setStatus?.(`Uploaded ${files.length} context file${files.length === 1 ? "" : "s"}.`, "ok");
}

function findContextItemFileInput(target) {
  const panel = target.closest("[data-context-panel]");
  const component = target.closest("[data-thread-component]");
  const form = target.closest("form");
  return panel?.querySelector("input[name='context_file']")
    || component?.querySelector("input[name='context_file']")
    || form?.querySelector("input[name='context_file']")
    || null;
}

async function insertContextItemIntoComposer(state, target, apiKey, options = {}) {
  const threadID = String(state?.thread?.id || "").trim();
  const contextItemID = contextItemIDFromTarget(target);
  if (!threadID || !contextItemID) {
    return;
  }
  let item = findContextItemByID(state, contextItemID);
  if (!String(item?.text || "").trim()) {
    const response = await apiGetJSON(apiKey, `/v1/threads/${encodeURIComponent(threadID)}/context-items/${encodeURIComponent(contextItemID)}`, {
      signal: options.signal
    });
    if (!requestsCurrent(options.request)) {
      return;
    }
    item = response?.context_item || item;
    if (item?.id) {
      upsertContextItem(state, item);
    }
  }
  const text = String(item?.text || "").trim();
  if (!text) {
    return;
  }
  state.composerDraft = state.composerDraft
    ? `${state.composerDraft.trimEnd()}\n\n${text}`
    : text;
  options.render?.();
  options.setStatus?.("Inserted context text.", "ok");
}

async function openContextItemPreview(state, target, apiKey, options = {}) {
  const threadID = String(state?.thread?.id || "").trim();
  const contextItemID = contextItemIDFromTarget(target);
  if (!threadID || !contextItemID) {
    return;
  }
  let item = findContextItemByID(state, contextItemID);
  state.contextItemPreviewID = contextItemID;
  state.contextItemPreviewError = "";
  state.contextItemPreviewLoading = !String(item?.text || "").trim();
  options.render?.();
  if (String(item?.text || "").trim()) {
    return;
  }
  try {
    const response = await apiGetJSON(apiKey, `/v1/threads/${encodeURIComponent(threadID)}/context-items/${encodeURIComponent(contextItemID)}`, {
      signal: options.signal
    });
    if (!requestsCurrent(options.request)) {
      return;
    }
    item = response?.context_item || item;
    if (item?.id) {
      upsertContextItem(state, item);
    }
    if (String(state.contextItemPreviewID || "").trim() === contextItemID) {
      state.contextItemPreviewLoading = false;
      state.contextItemPreviewError = "";
      options.render?.();
    }
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(options.request)) {
      return;
    }
    if (String(state.contextItemPreviewID || "").trim() === contextItemID) {
      state.contextItemPreviewLoading = false;
      state.contextItemPreviewError = error.message || "Unable to load context item.";
      options.render?.();
    }
  }
}

async function deleteContextItemFromThread(state, target, apiKey, options = {}) {
  const threadID = String(state?.thread?.id || "").trim();
  const contextItemID = contextItemIDFromTarget(target);
  if (!threadID || !contextItemID) {
    return;
  }
  await apiDelete(apiKey, `/v1/threads/${encodeURIComponent(threadID)}/context-items/${encodeURIComponent(contextItemID)}`, {
    signal: options.signal
  });
  removeContextItemFromState(state, contextItemID);
  options.render?.();
  options.setStatus?.("Removed context item.", "ok");
}

function upsertContextItem(state, item) {
  const id = String(item?.id || "").trim();
  if (!id) {
    return;
  }
  const current = Array.isArray(state.contextItems) ? state.contextItems : [];
  let replaced = false;
  state.contextItems = current.map((existing) => {
    if (String(existing?.id || "").trim() !== id) {
      return existing;
    }
    replaced = true;
    return { ...existing, ...item };
  });
  if (!replaced) {
    state.contextItems = [item, ...state.contextItems];
  }
}

function removeContextItemFromState(state, contextItemID) {
  const normalizedID = String(contextItemID || "").trim();
  if (!normalizedID) {
    return;
  }
  state.contextItems = (state.contextItems || []).filter((item) => String(item?.id || "").trim() !== normalizedID);
  state.composerContextRefs = (state.composerContextRefs || []).filter((item) =>
    !(String(item?.type || "").trim() === "context_item" && String(item.context_item_id || "").trim() === normalizedID)
  );
  if (state.thread) {
    state.thread = {
      ...state.thread,
      pinned_context_refs: (state.thread.pinned_context_refs || []).filter((item) =>
        !(String(item?.type || "").trim() === "context_item" && String(item.context_item_id || "").trim() === normalizedID)
      )
    };
  }
  if (String(state.contextItemDeleteConfirmID || "").trim() === normalizedID) {
    state.contextItemDeleteConfirmID = "";
  }
  if (String(state.contextItemPreviewID || "").trim() === normalizedID) {
    state.contextItemPreviewID = "";
    state.contextItemPreviewLoading = false;
    state.contextItemPreviewError = "";
  }
}

function buildOutputTextRef(state, target) {
  const turnID = String(target.getAttribute("data-turn-id") || "").trim();
  const outputIndex = Number.parseInt(String(target.getAttribute("data-output-index") || ""), 10);
  if (!turnID || Number.isNaN(outputIndex)) {
    return null;
  }
  const turn = (state.turns || []).find((item) => item?.id === turnID);
  const part = Array.isArray(turn?.output_parts) ? turn.output_parts[outputIndex] : null;
  const text = typeof part?.text === "string" ? part.text.trim() : "";
  if (!text) {
    return null;
  }
  return {
    type: "turn_output_text",
    turn_id: turnID,
    output_index: outputIndex,
    text
  };
}

function buildAttachmentRef(state, target) {
  const attachmentID = String(target.getAttribute("data-attachment-id") || "").trim();
  if (!attachmentID) {
    return null;
  }
  const attachment = findAttachmentByID(state, attachmentID);
  if (!attachment) {
    return null;
  }
  return {
    type: "attachment",
    attachment_id: attachmentID,
    attachment
  };
}

function buildContextItemRef(state, target) {
  const contextItemID = contextItemIDFromTarget(target);
  if (!contextItemID) {
    return null;
  }
  return {
    type: "context_item",
    context_item_id: contextItemID,
    context_item: findContextItemByID(state, contextItemID)
  };
}

function buildContextItemRefFromItem(item) {
  const contextItemID = String(item?.id || "").trim();
  if (!contextItemID) {
    return null;
  }
  return {
    type: "context_item",
    context_item_id: contextItemID,
    context_item: item
  };
}

function contextItemIDFromTarget(target) {
  return String(target.getAttribute("data-context-item-id") || target.getAttribute("data-context_item_id") || "").trim();
}

function findContextItemByID(state, contextItemID) {
  return (state.contextItems || []).find((item) => String(item?.id || "").trim() === contextItemID) || null;
}

function titleFromFilename(filename) {
  const value = String(filename || "Context file").split(/[\\/]/).pop() || "Context file";
  return value.replace(/\.(txt|md|markdown)$/i, "").trim() || value;
}

function findAttachmentByID(state, attachmentID) {
  for (const ref of state?.thread?.pinned_context_refs || []) {
    if (String(ref?.attachment_id || ref?.attachment?.id || "").trim() === attachmentID && ref?.attachment) {
      return ref.attachment;
    }
  }
  for (const turn of state?.turns || []) {
    for (const part of turn?.input_parts || []) {
      if (String(part?.attachment_id || part?.attachment?.id || "").trim() === attachmentID && part?.attachment) {
        return part.attachment;
      }
    }
    for (const part of turn?.output_parts || []) {
      if (String(part?.attachment_id || part?.attachment?.id || "").trim() === attachmentID && part?.attachment) {
        return part.attachment;
      }
    }
  }
  return null;
}

function addComposerContextRef(state, ref) {
  if (composerContextRefBlockReason(state, ref)) {
    return false;
  }
  state.composerContextRefs = [...(state.composerContextRefs || []), ref];
  return true;
}

function composerContextRefBlockReason(state, ref) {
  if (!ref) {
    return "invalid";
  }
  if (isPinnedContextRef(state.thread, ref) || hasComposerContextRef(state, ref)) {
    return "active";
  }
  if (ref.type === "context_item" && activeContextItemIDSet(state).size >= MAX_LLM_CONTEXT_ITEM_REFS) {
    return "limit";
  }
  return "";
}

function activeContextItemIDSet(state) {
  const ids = new Set();
  const collect = (refs) => {
    for (const item of refs || []) {
      if (item?.type !== "context_item") {
        continue;
      }
      const id = String(item.context_item_id || "").trim();
      if (id) {
        ids.add(id);
      }
    }
  };
  collect(state?.thread?.pinned_context_refs);
  collect(state?.composerContextRefs);
  return ids;
}

function contextItemLimitMessage() {
  return `Context limit reached: use at most ${MAX_LLM_CONTEXT_ITEM_REFS} context items per turn. Clear used items to add more.`;
}

function removeComposerContextRef(state, ref) {
  const key = contextRefKey(ref);
  const current = state.composerContextRefs || [];
  const next = current.filter((item) => contextRefKey(item) !== key);
  if (next.length === current.length) {
    return false;
  }
  state.composerContextRefs = next;
  return true;
}

function removeComposerContextItemsByID(state, contextItemIDs) {
  if (!contextItemIDs || contextItemIDs.size === 0) {
    return 0;
  }
  const current = state.composerContextRefs || [];
  const next = current.filter((item) =>
    item?.type !== "context_item" || !contextItemIDs.has(String(item.context_item_id || "").trim())
  );
  const removedCount = current.length - next.length;
  if (removedCount > 0) {
    state.composerContextRefs = next;
  }
  return removedCount;
}

function hasComposerContextRef(state, ref) {
  const key = contextRefKey(ref);
  return (state.composerContextRefs || []).some((item) => contextRefKey(item) === key);
}

function isPinnedContextRef(threadItem, ref) {
  const key = contextRefKey(ref);
  return (threadItem?.pinned_context_refs || []).some((item) => contextRefKey(item) === key);
}

function buildContextRefFromDataset(target) {
  const type = String(target.getAttribute("data-ref-type") || "").trim();
  if (type === "attachment") {
    const attachmentID = String(target.getAttribute("data-attachment-id") || "").trim();
    if (!attachmentID) {
      return null;
    }
    return { type, attachment_id: attachmentID };
  }
  if (type === "turn_output_text") {
    const turnID = String(target.getAttribute("data-turn-id") || "").trim();
    const outputIndex = Number.parseInt(String(target.getAttribute("data-output-index") || ""), 10);
    if (!turnID || Number.isNaN(outputIndex)) {
      return null;
    }
    return { type, turn_id: turnID, output_index: outputIndex };
  }
  if (type === "context_item") {
    const contextItemID = contextItemIDFromTarget(target);
    if (!contextItemID) {
      return null;
    }
    return { type, context_item_id: contextItemID };
  }
  return null;
}

function serializeContextRef(ref) {
  if (String(ref?.type || "").trim() === "attachment") {
    return {
      type: "attachment",
      attachment_id: String(ref?.attachment_id || "").trim()
    };
  }
  if (String(ref?.type || "").trim() === "context_item") {
    return {
      type: "context_item",
      context_item_id: String(ref?.context_item_id || "").trim()
    };
  }
  return {
    type: "turn_output_text",
    turn_id: String(ref?.turn_id || "").trim(),
    output_index: Number(ref?.output_index || 0)
  };
}

function contextRefKey(ref) {
  if (String(ref?.type || "").trim() === "attachment") {
    return `attachment:${String(ref?.attachment_id || "").trim()}`;
  }
  if (String(ref?.type || "").trim() === "context_item") {
    return `context_item:${String(ref?.context_item_id || "").trim()}`;
  }
  return `turn_output_text:${String(ref?.turn_id || "").trim()}:${Number(ref?.output_index || 0)}`;
}

function pendingAttachmentsForCapability(state, capability) {
  const mediaKind = mediaKindForCapability(capability);
  return (state.pendingAttachments || []).filter((item) => item?.media_kind === mediaKind);
}

function prunePendingAttachmentsForCapability(state, capability) {
  const keepKind = mediaKindForCapability(capability);
  if (keepKind !== "audio" && threadCaptureSession(state)) {
    clearThreadCaptureSession(state, { cancel: true });
    state.captureState = {
      kind: "",
      status: "idle",
      source: ""
    };
  }
  if (!keepKind) {
    clearPendingAttachments(state);
    return;
  }
  const removeIDs = (state.pendingAttachments || [])
    .filter((item) => item?.media_kind !== keepKind)
    .map((item) => item.id);
  clearPendingAttachments(state, removeIDs);
}

function mediaKindForCapability(capability) {
  return inferenceCapabilityMediaKind(capability);
}

function fileInputSource(target) {
  return target.name === "audio" ? "upload" : "upload";
}

function addPendingFiles(state, files, source, capability) {
  const mediaKind = mediaKindForCapability(capability);
  if (!mediaKind) {
    return [];
  }
  const nextItems = files.map((file, index) => createPendingAttachment(state, file, source, mediaKind, index));
  if (mediaKind === "audio") {
    clearPendingAttachments(state);
    state.pendingAttachments = nextItems.slice(-1);
    return state.pendingAttachments;
  }
  state.pendingAttachments = [...(state.pendingAttachments || []), ...nextItems];
  return nextItems;
}

function createPendingAttachment(state, file, source, mediaKind, index) {
  const pendingID = `pending-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
  rememberPendingAttachmentFile(state, pendingID, file);
  return {
    id: pendingID,
    origin: source,
    media_kind: mediaKind,
    filename: String(file?.name || `${mediaKind}-${index + 1}`),
    content_type: String(file?.type || ""),
    size_bytes: Number(file?.size || 0),
    object_url: URL.createObjectURL(file)
  };
}

function clearPendingAttachments(state, ids = null) {
  const removeIDs = new Set(ids || (state.pendingAttachments || []).map((item) => item.id));
  if (removeIDs.size === 0) {
    return;
  }
  for (const item of state.pendingAttachments || []) {
    if (!removeIDs.has(item.id)) {
      continue;
    }
    forgetPendingAttachmentFile(state, item.id);
    revokePendingObjectURL(item.object_url);
  }
  state.pendingAttachments = (state.pendingAttachments || []).filter((item) => !removeIDs.has(item.id));
}

function removePendingAttachment(state, pendingID) {
  clearPendingAttachments(state, [pendingID]);
}

function revokePendingObjectURL(objectURL) {
  const value = String(objectURL || "").trim();
  if (!value) {
    return;
  }
  URL.revokeObjectURL(value);
}

export function handleLLMThreadComponentDragDrop(state, event, options = {}) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return false;
  }
  if (!canUploadAttachmentPermission(options.contextPermissions)) {
    return false;
  }
  const dropTarget = target.closest(".composer-v2--droppable");
  if (!dropTarget) {
    return false;
  }
  const capability = normalizeInferenceCapability(state.composerCapability);
  const mediaKind = inferenceCapabilityMediaKind(capability);
  if (!mediaKind) {
    return false;
  }
  if (event.type === "dragover") {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    dropTarget.classList.add("composer-v2--drag-active");
    return true;
  }
  if (event.type === "dragleave") {
    if (!dropTarget.contains(event.relatedTarget)) {
      dropTarget.classList.remove("composer-v2--drag-active");
    }
    return true;
  }
  if (event.type === "drop") {
    event.preventDefault();
    dropTarget.classList.remove("composer-v2--drag-active");
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return true;
    }
    const accept = inferenceCapabilityFileAccept(capability);
    const filtered = [...files].filter((file) => matchesAccept(file, accept));
    if (filtered.length > 0) {
      addPendingFiles(state, filtered, "upload", capability);
      options.render?.();
      options.setStatus?.(`Added ${filtered.length} file(s).`, "ok");
    }
    return true;
  }
  return false;
}

function matchesAccept(file, accept) {
  if (!accept) {
    return true;
  }
  const types = accept.split(",").map((type) => type.trim().toLowerCase());
  const fileType = String(file.type || "").toLowerCase();
  return types.some((type) => fileType === type || (type.endsWith("/*") && fileType.startsWith(type.slice(0, -1))));
}
